import React, { useCallback, useMemo, useState } from "react";
import { BaseModal, IModalInstance, ModalHeader, modalService } from "@whatslly/ui-components/modals";
import styles from "./AddContactViaCSVModal.module.scss";
import { useTranslation } from "react-i18next";
import classnames from "classnames";
import { downloadBlob } from "@whatslly/ui-components/componentsUtils";
import { arrayToCsv, parseCSV } from "@whatslly/commons/csvUtils";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import ms from "ms";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import globalStyles from "@whatslly/ui-components/v2/globalStyles";
import { trimObjectStrings, calFileSizeV2 } from "@whatslly/commons/utils";
import { Text } from "@whatslly/ui-components/Text";
import { FaTrash } from "react-icons/fa";
import { Table } from "@whatslly/ui-components/v2/Tables/Table/Table";
import { FileDropzone } from "@whatslly/ui-components/v2/FileDropzone";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import { Button } from "@whatslly/ui-components/v2/Button";
import type { ContactToImportFromCsvType } from "@whatslly/commons/tuvis-messaging/messagingCommands";
import { isPhoneNumber } from "@whatslly/commons/phoneUtils";

const cols = [
  {
    name: "First Name",
    required: true,
  },
  {
    name: "Last Name",
    required: true,
  },
  {
    name: "Phone",
    required: true,
  },
];

const colsNames = cols.map((contact) => contact.name);

const colsReqNames = cols.filter((contact) => contact.required).map((contact) => contact.name);

const previewCols = cols.map((col) => {
  return {
    keyInsideItem: col.name,
    title: col.name,
  };
});

const downloadCsvTemplate = () => downloadBlob(arrayToCsv([[...colsNames]]), `tuvis-csv-template.csv`, `text/csv;charset=utf-8;`);

export const AddContactViaCSVModal: React.FC<IModalInstance<{ contacts: ContactToImportFromCsvType[] | undefined }>> = ({ closeModal }) => {
  const { t } = useTranslation();
  const [csvContent, setCsvContent] = useState<{ header: string[]; data: Record<string, string>[] }>();
  const [selectedFile, setSelectedFile] = useState<File | undefined>();
  const res = useMemo<ContactToImportFromCsvType[] | undefined>(() => {
    if (!csvContent?.data.length) return undefined;
    return csvContent.data.map((contact) => ({ firstName: contact["First Name"], lastName: contact["Last Name"], phone: contact["Phone"] }));
  }, [csvContent]);

  const onFileSelect = useCallback(
    (csvContent: string, file: File) => {
      const { data, header } = parseCSV(csvContent);
      const trimmedData = trimObjectStrings(data);

      const invalidHeaders = header.filter((contact) => !colsNames.includes(contact));
      const missingRequiredHeaders = colsReqNames.filter((contact) => !header.includes(contact));
      const invalidPhones = trimmedData.filter((contact) => !isPhoneNumber(contact["Phone"])).map(({ Phone }) => Phone);
      if (data.length === 0) {
        modalService.openModal(Toast, {
          text: t("user_management.csv_errors.empty_file"),
          type: `error`,
          timeout: ms("1min"),
        });
        return;
      }
      if (missingRequiredHeaders.length > 0) {
        modalService.openModal(Toast, {
          text: t("user_management.csv_errors.missing_required_headers", { missingRequiredHeaders: missingRequiredHeaders.join(", ") }),
          type: `error`,
          timeout: ms("1min"),
        });
        return;
      }
      if (invalidHeaders.length > 0) {
        modalService.openModal(Toast, {
          text: t("user_management.csv_errors.invalid_headers", { invalidHeaders }),
          type: `error`,
          timeout: ms("1min"),
        });
        return;
      }
      if (invalidPhones.length > 0) {
        modalService.openModal(Toast, {
          text: t("user_management.csv_errors.invalid_phones", { invalidPhones: invalidPhones.join(", ") }),
          type: `error`,
          timeout: ms("1min"),
        });
        return;
      }

      const missingFirstName = trimmedData.filter((contact) => !contact["First Name"]);

      if (missingFirstName.length > 0) {
        modalService.openModal(Toast, {
          text: t("user_management.csv_errors.missing_first_name", {
            missingFirstName: missingFirstName.map((contact) => `${contact["Last Name"] || contact["Phone"]}`).join(", "),
          }),
          type: `error`,
          timeout: ms("1min"),
        });
        return;
      }

      const missingLastName = trimmedData.filter((contact) => !contact["Last Name"]);

      if (missingLastName.length > 0) {
        modalService.openModal(Toast, {
          text: t("user_management.csv_errors.missing_last_name", { missingLastName: missingLastName.map((contact) => contact["First Name"]) }),
          type: `error`,
          timeout: ms("1min"),
        });
        return;
      }

      setCsvContent({ data: trimmedData, header });
      setSelectedFile(file);
    },
    [colsNames, colsReqNames, t]
  );

  return (
    <BaseModal
      closeOnClickOutside={false}
      closeOnEscape={false}
      closeModal={closeModal}
      className={styles.importContactsModal}
      bodyClassName={styles.importContactsModalBody}
      header={<ModalHeader closeModal={() => closeModal()}>{t("user_management.import_users_csv_button")}</ModalHeader>}
      footer={
        <ModalButtons
          submitText={t("user_management.import_button")}
          testId={`modal-contact-button`}
          onCancel={() => closeModal()}
          isSubmitDisabled={!selectedFile}
          onSubmit={() => closeModal({ contacts: res })}
        />
      }
    >
      <div>{t("user_management.import_users_modal_text")}</div>
      <ul>
        {cols.map((col) => {
          return <li key={col.name}>{`${col.name}${col.required ? ` (${t("user_management.required")})` : ""}`}</li>;
        })}
      </ul>
      <div>
        <Button
          secondary
          data-testid={`download-csv-template-button`}
          onClick={downloadCsvTemplate}
          paddingInline={`small`}
          text={t("user_management.import_users_csv_template")}
        />
      </div>
      {selectedFile && csvContent ? (
        <div>
          <div
            className={classnames(
              styles.clearAndText,
              globalStyles["bg-color-secondary-primary"],
              globalStyles["bc-neutral-secondary"],
              globalStyles["color-main-primary"]
            )}
          >
            <div className={styles.textContainer}>
              <div className={styles.fileNameText}>{selectedFile.name}</div>
              <p className={classnames(styles.sizeText, globalStyles["color-neutral-primary"])}>({calFileSizeV2(selectedFile)})</p>
            </div>
            <div
              className={styles.clearIconContainer}
              onClick={() => {
                setCsvContent(undefined);
                setSelectedFile(undefined);
              }}
            >
              <Text className={classnames(styles.clearIcon, globalStyles["color-main-primary"])}>
                <FaTrash width="14" height="14"></FaTrash>
              </Text>
            </div>
          </div>
          <div className={styles.previewTableContainer}>
            <div>{t("user_management.import_users_preview")}</div>
            <Table className={styles.previewTable} items={csvContent?.data} emptyStateNode={<></>} columns={previewCols} />
          </div>
        </div>
      ) : (
        <FileDropzone accept=".csv" mode="string" className={styles.dropzone} onFile={onFileSelect}>
          <SvgV2 iconName={"upload" as any} height={25} />
          <div>{t("user_management.drop_csv")}</div>
        </FileDropzone>
      )}
    </BaseModal>
  );
};
